import { Link } from "react-router-dom";
import { ArrowRight, Shield, Target, Radar, FileCheck, Cog } from "lucide-react";
import { motion } from "framer-motion";

const capabilities = [
  {
    title: "Counter-UAS Capability Assessment",
    description: "Use UAVSimNet to test scenarios, quantify uncertainty and compare interceptor options before committing to live trials or procurement decisions.",
    icon: Target,
  },
  {
    title: "Hardware-Neutral Mission Integration",
    description: "Integrate sensors, effectors and operator systems from multiple vendors through governed interfaces. No vendor lock-in.",
    icon: Cog,
  },
  {
    title: "AI-AV Autonomous Integration",
    description: "Where vision-based autonomous control is required, AI-AV provides target-oriented guidance designed to operate without GPS.",
    icon: Radar,
  },
  {
    title: "Evidence & Test Planning",
    description: "Every capability assessment comes with evidence packs, model governance documentation and test planning support.",
    icon: FileCheck,
  },
];

export default function DefenceCUASPage() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/solutions" className="text-caption hover:text-foreground transition-colors mb-4 inline-block">
              Solutions
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center">
                <Shield className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
            <h1 className="text-display text-foreground mb-6">
              Defence & Counter-UAS
            </h1>
            <p className="text-subtitle max-w-2xl">
              RLK is a mission-systems and analytical technology partner, not a catalogue of autonomous UAV products. We help defence teams understand, evaluate and coordinate complex counter-UAS operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Positioning */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <h2 className="text-headline text-foreground mb-6">
              Mission Systems, Not Just Products
            </h2>
            <p className="text-body mb-4">
              Counter-UAS programmes require more than individual products. They need integrated mission systems that combine analysis, simulation, coordination and—where appropriate—autonomous capabilities.
            </p>
            <p className="text-body">
              RLK provides the analytical foundation and integration framework. Our products can be used independently or combined with qualified customer and partner systems through defined interfaces.
            </p>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Capabilities</p>
            <h2 className="text-headline text-foreground">What We Provide</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {capabilities.map((cap, index) => (
              <motion.div
                key={index}
                className="card-premium p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center mb-4">
                  <cap.icon className="h-5 w-5 text-muted-foreground" />
                </div>
                <h3 className="text-label text-foreground mb-2">{cap.title}</h3>
                <p className="text-sm text-muted-foreground">{cap.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Relevant Platforms */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Relevant Platforms</p>
            <h2 className="text-headline text-foreground">Technology Stack</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/platforms/uavsimnet" className="group">
              <div className="card-pillar p-6 h-full">
                <h3 className="text-label text-foreground mb-2 group-hover:text-foreground/80 transition-colors">UAVSimNet</h3>
                <p className="text-sm text-muted-foreground mb-4">Simulation and decision support for capability assessment and test planning.</p>
                <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                  Learn more →
                </span>
              </div>
            </Link>
            <Link to="/platforms/ai-av" className="group">
              <div className="card-pillar p-6 h-full">
                <h3 className="text-label text-foreground mb-2 group-hover:text-foreground/80 transition-colors">AI-AV</h3>
                <p className="text-sm text-muted-foreground mb-4">Vision-based autonomous guidance designed for GPS-denied operation.</p>
                <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                  Learn more →
                </span>
              </div>
            </Link>
            <Link to="/platforms/advanced-development" className="group">
              <div className="card-pillar p-6 h-full">
                <h3 className="text-label text-foreground mb-2 group-hover:text-foreground/80 transition-colors">Design Assets</h3>
                <p className="text-sm text-muted-foreground mb-4">Selected airframe programmes and licensable design assets.</p>
                <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                  Learn more →
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-headline text-foreground mb-6">
              Discuss Your Programme
            </h2>
            <p className="text-body mb-8">
              Whether you need capability assessment, mission integration support, or autonomous technology evaluation, we can help define the right approach.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?type=defence" className="btn-primary">
                Request a Defence Briefing
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
              <Link to="/platforms/uavsimnet" className="btn-secondary">
                Scope an Assessment
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
